/**
 * Sistema de logging profissional para a aplicação
 *
 * REGRAS DE NEGÓCIO:
 * - Logs só aparecem em desenvolvimento para proteger informações sensíveis
 * - Em produção, erros críticos podem ser enviados para serviços de monitoramento
 * - Níveis de severidade: DEBUG < INFO < WARN < ERROR < CRITICAL
 *
 * @module utils/logger
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

/**
 * Níveis de log para controle granular
 * @enum {number}
 */
const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  CRITICAL: 4,
};

/**
 * Formata mensagem de log com timestamp e contexto
 * @param {string} level - Nível do log
 * @param {string} message - Mensagem principal
 * @param {Object} context - Contexto adicional
 * @returns {string} Mensagem formatada
 */
const formatMessage = (level, message, context = {}) => {
  const timestamp = new Date().toISOString();
  const contextStr = Object.keys(context).length
    ? ` | ${JSON.stringify(context)}`
    : '';
  return `[${timestamp}] [${level}] ${message}${contextStr}`;
};

/**
 * Envia logs críticos para serviço de monitoramento em produção
 * @param {string} level - Nível do log
 * @param {string} message - Mensagem de erro
 * @param {Object} metadata - Metadados adicionais
 */
const sendToMonitoring = (level, message, metadata = {}) => {
  // Integração futura com Sentry, LogRocket, etc.
  // TODO: Implementar envio real quando serviço for configurado
  if (!isDevelopment && level === 'CRITICAL') {
    // Preparação do payload para envio futuro
    const timestamp = new Date().toISOString();
    const userAgent =
      typeof window !== 'undefined' ? window.navigator.userAgent : 'server';
    const url = typeof window !== 'undefined' ? window.location.href : 'server';

    // fetch('/api/logs', {
    //   method: 'POST',
    //   body: JSON.stringify({ level, message, metadata, timestamp, userAgent, url })
    // })

    // Por enquanto, apenas valida que os dados estão prontos
    void { level, message, metadata, timestamp, userAgent, url };
  }
};

/**
 * Logger centralizado com múltiplas funcionalidades
 * @namespace
 */
const logger = {
  /**
   * Log de debug para desenvolvimento
   * @param {string} message - Mensagem de debug
   * @param {Object} context - Contexto adicional
   */
  debug: (message, context = {}) => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.debug(formatMessage('DEBUG', message, context));
    }
  },

  /**
   * Log informativo
   * @param {string} message - Mensagem informativa
   * @param {Object} context - Contexto adicional
   */
  info: (message, context = {}) => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.info(formatMessage('INFO', message, context));
    }
  },

  /**
   * Log de aviso
   * @param {string} message - Mensagem de aviso
   * @param {Object} context - Contexto adicional
   */
  warn: (message, context = {}) => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.warn(formatMessage('WARN', message, context));
    }
    sendToMonitoring('WARN', message, context);
  },

  /**
   * Log de erro
   * @param {string} message - Mensagem de erro
   * @param {Error|Object} error - Objeto de erro ou contexto
   */
  error: (message, error = {}) => {
    const errorContext =
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error;

    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.error(formatMessage('ERROR', message, errorContext));
    }
    sendToMonitoring('ERROR', message, errorContext);
  },

  /**
   * Log crítico para falhas graves
   * @param {string} message - Mensagem crítica
   * @param {Error|Object} error - Objeto de erro ou contexto
   */
  critical: (message, error = {}) => {
    const errorContext =
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error;

    if (!isTest) {
      // eslint-disable-next-line no-console
      console.error(formatMessage('CRITICAL', message, errorContext));
    }
    sendToMonitoring('CRITICAL', message, errorContext);
  },

  /**
   * Mede performance de operações
   * @param {string} label - Identificador da operação
   */
  time: label => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.time(label);
    }
  },

  /**
   * Finaliza medição de performance
   * @param {string} label - Identificador da operação
   */
  timeEnd: label => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.timeEnd(label);
    }
  },

  /**
   * Cria tabela para visualização de dados
   * @param {Array|Object} data - Dados para tabular
   */
  table: data => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.table(data);
    }
  },

  /**
   * Agrupa logs relacionados
   * @param {string} label - Nome do grupo
   */
  group: label => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.group(label);
    }
  },

  /**
   * Finaliza grupo de logs
   */
  groupEnd: () => {
    if (isDevelopment && !isTest) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  },

  // Alias para compatibilidade
  log: (...args) => logger.info(...args),
};

export default logger;
export { LogLevel };
